import React from 'react'
import { useQuery } from 'react-query'
import { Plus, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { NotionBlogEditor } from './NotionBlogEditor'
import { BlogImageUploader } from './BlogImageUploader'
import { authorService } from '@/services/authorService'

const defaultCategories = ['Design', 'AI', 'Technology']

function splitList(value = '') {
  return value.split(',').map((item) => item.trim()).filter(Boolean)
}

function joinList(items) {
  return Array.from(new Set(items)).join(', ')
}

export function slugify(value = '') {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function sanitizeSlugInput(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9-]+/g, '-')
}

export function BlogPostForm({ formId, draft, setDraft, uploading, onUpload, onSubmit, isEditing }) {
  const [categoryName, setCategoryName] = React.useState('')
  const [slugEdited, setSlugEdited] = React.useState(false)
  const { data: authorsData } = useQuery('authors', authorService.getAllAuthors)
  const authors = authorsData?.data?.authors || []
  const selectedAuthor = React.useMemo(
    () => authors.find((author) => author.id === draft.author_id),
    [authors, draft.author_id]
  )

  async function handleCoverImageUpload(file) {
    const url = await onUpload(file)
    if (url) updateField('image', url)
  }
  const selectedCategories = React.useMemo(() => splitList(draft.category), [draft.category])
  const categoryOptions = React.useMemo(
    () => Array.from(new Set([...defaultCategories, ...selectedCategories])),
    [selectedCategories]
  )

  function updateField(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  function toggleCategory(category) {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter((item) => item !== category)
      : [...selectedCategories, category]
    updateField('category', joinList(next))
  }

  function addCategory() {
    const category = categoryName.trim()
    if (!category) return
    updateField('category', joinList([...selectedCategories, category]))
    setCategoryName('')
  }

  return (
    <form id={formId} className="grid gap-6 lg:grid-cols-[1fr_320px]" onSubmit={(event) => { event.preventDefault(); onSubmit() }}>
      <div className="space-y-4">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Title</label>
              <Input
                value={draft.title}
                onChange={(event) => {
                  const value = event.target.value
                  updateField('title', value)
                  if (!isEditing && !slugEdited) updateField('id', slugify(value))
                }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Slug</label>
              <Input
                value={draft.id}
                onChange={(event) => {
                  const value = sanitizeSlugInput(event.target.value)
                  setSlugEdited(value.length > 0)
                  updateField('id', value)
                }}
                onBlur={(event) => updateField('id', slugify(event.target.value))}
                placeholder="my-blog-post"
                required
              />
              {isEditing && (
                <p className="text-xs text-muted-foreground">Changing the slug changes the post URL. The old slug will keep redirecting to this post.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Excerpt</label>
              <Textarea rows={2} value={draft.excerpt} onChange={(event) => updateField('excerpt', event.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Published date</label>
                <Input type="date" value={draft.published_date} onChange={(event) => updateField('published_date', event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Read time (min)</label>
                <Input type="number" min={0} value={draft.read_time} onChange={(event) => updateField('read_time', Number(event.target.value) || 0)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Category</label>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categoryOptions.map((category) => {
                  const selected = selectedCategories.includes(category)
                  return (
                    <button
                      type="button"
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium ${selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input hover:bg-accent'}`}
                    >
                      {category}
                    </button>
                  )
                })}
              </div>
              <div className="flex gap-1.5">
                <Input
                  value={categoryName}
                  onChange={(event) => setCategoryName(event.target.value)}
                  placeholder="New category"
                  onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addCategory() } }}
                />
                <Button type="button" variant="outline" onClick={addCategory}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Author</label>
              <Select
                value={draft.author_id ? String(draft.author_id) : 'none'}
                onValueChange={(value) => updateField('author_id', value === 'none' ? null : Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an author" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No author</SelectItem>
                  {authors.map((author) => (
                    <SelectItem key={author.id} value={String(author.id)}>{author.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAuthor?.role?.name ? (
                <p className="text-xs text-muted-foreground">Role: {selectedAuthor.role.name}</p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Content</CardTitle>
          </CardHeader>
          <CardContent>
            <NotionBlogEditor
              contentBlocks={draft.content_blocks}
              onChange={(blocks) => updateField('content_blocks', blocks)}
              uploading={uploading}
              onUpload={onUpload}
            />
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Cover image</label>
              <BlogImageUploader
                image={draft.image}
                imageAlt={draft.image_alt}
                uploading={uploading}
                onUpload={handleCoverImageUpload}
                onImageChange={(value) => updateField('image', value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Image alt text</label>
              <Input value={draft.image_alt} onChange={(event) => updateField('image_alt', event.target.value)} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">SEO metadata</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Meta title</label>
              <Input value={draft.meta_title} onChange={(event) => updateField('meta_title', event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Meta description</label>
              <Textarea rows={3} value={draft.meta_description} onChange={(event) => updateField('meta_description', event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Meta keywords</label>
              <div className="flex flex-wrap gap-1.5 rounded-md border border-input p-2">
                {splitList(draft.meta_keywords).map((keyword) => (
                  <span key={keyword} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs">
                    {keyword}
                    <button type="button" onClick={() => updateField('meta_keywords', joinList(splitList(draft.meta_keywords).filter((item) => item !== keyword)))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <input
                  className="min-w-[80px] flex-1 bg-transparent text-xs outline-none"
                  placeholder="Add keyword"
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ',') return
                    event.preventDefault()
                    const value = event.currentTarget.value.trim()
                    if (!value) return
                    updateField('meta_keywords', joinList([...splitList(draft.meta_keywords), value]))
                    event.currentTarget.value = ''
                  }}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Canonical URL</label>
              <Input value={draft.canonical_url} onChange={(event) => updateField('canonical_url', event.target.value)} />
            </div>
          </CardContent>
        </Card>
      </div>
    </form>
  )
}
